import type { AttributesView, FeedbackView } from '@champdle/contracts';

export type AttrKey = keyof AttributesView;
export type MatchKind = 'correct' | 'partial' | 'incorrect';

export const COLUMNS: readonly { key: AttrKey; label: string }[] = [
  { key: 'gender', label: 'Gender' },
  { key: 'positions', label: 'Position' },
  { key: 'species', label: 'Species' },
  { key: 'resource', label: 'Resource' },
  { key: 'rangeType', label: 'Range' },
  { key: 'regions', label: 'Region' },
  { key: 'releaseYear', label: 'Release year' },
];

export function matchOf(feedback: FeedbackView, key: AttrKey): MatchKind {
  return key === 'releaseYear' ? feedback.releaseYear.result : feedback[key];
}

export function valuesOf(attributes: AttributesView, key: AttrKey): string[] {
  const v = attributes[key];
  return Array.isArray(v) ? v : [String(v)];
}

const MATCH_WORD: Record<MatchKind, string> = {
  correct: 'correct match',
  partial: 'partial match',
  incorrect: 'no match',
};

/** The sentence a screen reader gets for one tile; colour is never the only signal. */
export function describeTile(
  label: string,
  values: readonly string[],
  match: MatchKind,
  arrow: 'up' | 'down' | null,
): string {
  const direction =
    arrow === 'up' ? ', the champion is newer' : arrow === 'down' ? ', the champion is older' : '';
  return `${label}: ${values.join(', ')}, ${MATCH_WORD[match]}${direction}`;
}

const SQUARE: Record<MatchKind, string> = { correct: '🟩', partial: '🟨', incorrect: '🟥' };

/** Spoiler-free share text: one row per guess, one square per attribute, no names. */
export function shareText(
  guesses: readonly { feedback: FeedbackView }[],
  opts: { won: boolean; elapsedMs: number; url: string },
): string {
  const head = opts.won
    ? `LoLdle Multi: solved in ${guesses.length} ${guesses.length === 1 ? 'guess' : 'guesses'} (${formatDuration(opts.elapsedMs)})`
    : `LoLdle Multi: gave up after ${guesses.length} ${guesses.length === 1 ? 'guess' : 'guesses'}`;
  const rows = guesses.map((g) => COLUMNS.map((c) => SQUARE[matchOf(g.feedback, c.key)]).join(''));
  return [head, '', ...rows, '', opts.url].join('\n');
}

export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(total / 60);
  const s = String(total % 60).padStart(2, '0');
  return `${m}:${s}`;
}
